import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import KPICard from '../components/KPICard';
import ClientPanel from '../components/ClientPanel';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useFilters } from '../hooks/useFilters';

const STATUT_COLORS = { Prospect: '#41B073', 'Client actif': '#22c55e', 'Client inactif': '#94a3b8', Perdu: '#ef4444' };
const TYPE_COLORS = { TPE: '#3b82f6', PME: '#41B073', ETI: '#8b5cf6', Partenaire: '#f59e0b' };

const Badge = ({ text, color }) => (
  <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{text}</span>
);

const inputStyle = { padding: '8px 12px', borderRadius: 8, background: '#1a1d2e', border: '1px solid #2a2d3e', color: '#e2e8f0', fontSize: 13, fontFamily: 'inherit', outline: 'none' };
const selectStyle = { ...inputStyle, cursor: 'pointer' };

export default function ClientsPage() {
  const fetchWithAuth = useAuthFetch();
  const location = useLocation();
  const { search, setSearch, page, setPage, limit, setLimit, sortField, sortDir, toggleSort, filters, setFilter, buildQuery } = useFilters({ sortField: 'nom' });
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(location.state?.clientId || null);
  const [showModal, setShowModal] = useState(false);
  const [newClient, setNewClient] = useState({ type: 'TPE', nom: '', secteur: '', ville: '', email: '', telephone: '', statut: 'Prospect' });
  const [formErrors, setFormErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`/api/clients?${buildQuery()}`);
      const data = await res.json();
      setClients(data.data || []);
      setTotal(data.total || 0);
      const all = await fetchWithAuth(`/api/clients?limit=999`).then(r => r.json());
      const counts = { total: 0, Prospect: 0, 'Client actif': 0, 'Client inactif': 0, Perdu: 0 };
      (all.data || []).forEach(c => { counts.total++; if (counts[c.statut] !== undefined) counts[c.statut]++; });
      setKpis(counts);
    } finally { setLoading(false); }
  }, [search, page, limit, sortField, sortDir, JSON.stringify(filters)]);

  useEffect(() => { load(); }, [load]);

  const createClient = async () => {
    const errors = {};
    if (!newClient.nom.trim()) errors.nom = true;
    if (!newClient.type) errors.type = true;
    if (Object.keys(errors).length) { setFormErrors(errors); return; }
    await fetchWithAuth('/api/clients', { method: 'POST', body: JSON.stringify(newClient) });
    setShowModal(false);
    setNewClient({ type: 'TPE', nom: '', secteur: '', ville: '', email: '', telephone: '', statut: 'Prospect' });
    setFormErrors({});
    load();
  };

  const SortTh = ({ field, children }) => (
    <th onClick={() => toggleSort(field)} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
      {children} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  const totalPages = Math.ceil(total / limit);

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Clients</h1>
        <button onClick={() => setShowModal(true)} style={{ padding: '9px 18px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>+ Nouveau client</button>
      </div>

      {/* KPIs */}
      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <KPICard title="Total" value={kpis.total || 0} color="#41B073" icon="👥" onClick={() => setFilter('statut', '')} />
        <KPICard title="Prospects" value={kpis['Prospect'] || 0} color="#41B073" onClick={() => setFilter('statut', 'Prospect')} />
        <KPICard title="Actifs" value={kpis['Client actif'] || 0} color="#22c55e" onClick={() => setFilter('statut', 'Client actif')} />
        <KPICard title="Inactifs" value={kpis['Client inactif'] || 0} color="#94a3b8" onClick={() => setFilter('statut', 'Client inactif')} />
        <KPICard title="Perdus" value={kpis['Perdu'] || 0} color="#ef4444" onClick={() => setFilter('statut', 'Perdu')} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Rechercher..." style={{ ...inputStyle, minWidth: 200 }} />
        <select value={filters.statut || ''} onChange={e => setFilter('statut', e.target.value)} style={selectStyle}>
          <option value="">Tous les statuts</option>
          {['Prospect', 'Client actif', 'Client inactif', 'Perdu'].map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} style={selectStyle}>
          <option value="">Tous les types</option>
          {['TPE', 'PME', 'ETI', 'Partenaire'].map(t => <option key={t}>{t}</option>)}
        </select>
        <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} style={selectStyle}>
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
        </select>
      </div>

      {/* Table */}
      <div style={{ background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2d3e' }}>
                <SortTh field="nom">Nom</SortTh>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Type</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Statut</th>
                <SortTh field="secteur">Secteur</SortTh>
                <SortTh field="ville">Ville</SortTh>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Commercial</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Chargement...</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Aucun client trouvé</td></tr>
              ) : clients.map((c, i) => (
                <tr key={c.id} onClick={() => setSelectedId(c.id)} style={{ borderBottom: '1px solid #2a2d3e', cursor: 'pointer', background: i % 2 === 0 ? 'transparent' : '#ffffff05', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#41B07315'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : '#ffffff05'}>
                  <td style={{ padding: '11px 14px', fontSize: 13, color: '#e2e8f0', fontWeight: 500 }}>{c.nom}</td>
                  <td style={{ padding: '11px 14px' }}><Badge text={c.type} color={TYPE_COLORS[c.type] || '#64748b'} /></td>
                  <td style={{ padding: '11px 14px' }}><Badge text={c.statut} color={STATUT_COLORS[c.statut] || '#64748b'} /></td>
                  <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{c.secteur || '—'}</td>
                  <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{c.ville || '—'}</td>
                  <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{c.commercial_prenom ? `${c.commercial_prenom} ${c.commercial_nom}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #2a2d3e' }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>{total} client{total !== 1 ? 's' : ''}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '5px 12px', borderRadius: 6, background: 'transparent', border: '1px solid #2a2d3e', color: page === 1 ? '#2a2d3e' : '#94a3b8', cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}>←</button>
            <span style={{ padding: '5px 12px', fontSize: 13, color: '#94a3b8' }}>Page {page} / {totalPages || 1}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ padding: '5px 12px', borderRadius: 6, background: 'transparent', border: '1px solid #2a2d3e', color: page >= totalPages ? '#2a2d3e' : '#94a3b8', cursor: page >= totalPages ? 'default' : 'pointer', fontSize: 13 }}>→</button>
          </div>
        </div>
      </div>

      {/* Panel */}
      {selectedId && <ClientPanel clientId={selectedId} onClose={() => setSelectedId(null)} />}

      {/* Create Modal */}
      {showModal && (
        <>
          <div onClick={() => setShowModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200 }} />
          <div className="modal-scroll" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 480, background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 14, padding: 28, zIndex: 201, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: '#e2e8f0', fontSize: 16 }}>Nouveau client</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            {[['nom', 'Nom *', 'text'], ['secteur', 'Secteur', 'text'], ['adresse', 'Adresse', 'text'], ['ville', 'Ville', 'text'], ['code_postal', 'Code postal', 'text'], ['telephone', 'Téléphone', 'text'], ['email', 'Email', 'email'], ['site_web', 'Site web', 'text']].map(([k, l, t]) => (
              <div key={k} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>{l}</label>
                <input type={t} value={newClient[k] || ''} onChange={e => setNewClient(f => ({ ...f, [k]: e.target.value }))} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', borderColor: formErrors[k] ? '#ef4444' : '#2a2d3e' }} />
              </div>
            ))}
            {[['type', 'Type *', ['TPE', 'PME', 'ETI', 'Partenaire']], ['statut', 'Statut', ['Prospect', 'Client actif', 'Client inactif', 'Perdu']]].map(([k, l, opts]) => (
              <div key={k} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>{l}</label>
                <select value={newClient[k]} onChange={e => setNewClient(f => ({ ...f, [k]: e.target.value }))} style={{ ...selectStyle, width: '100%', boxSizing: 'border-box' }}>
                  {opts.map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            ))}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Notes</label>
              <textarea value={newClient.notes || ''} onChange={e => setNewClient(f => ({ ...f, notes: e.target.value }))} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', height: 70, resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={createClient} style={{ flex: 1, padding: '10px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: 'inherit' }}>Créer le client</button>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', borderRadius: 8, background: 'transparent', border: '1px solid #2a2d3e', color: '#94a3b8', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>Annuler</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
